/**
 * Helpers purs pour filtrer les entries d'une autorisation qui sont des
 * **dépendances RLS auto-injectées par le backend** ( cf
 * {@code AuthorizationService.addDependantAuthorizations} côté Java ) .
 *
 * <p><b>Contexte</b> ( ticket #521 ) : le backend OpenADOM ajoute
 * automatiquement {@code extraction} sur les référentiels/datatypes
 * qui sont dépendances ( cross-data ) de ceux que l'utilisateur a
 * cochés . Ces entries sont nécessaires pour que les jointures FK
 * passent les policies RLS PostgreSQL ( sinon un {@code SELECT JOIN}
 * échoue ) mais elles sont transparentes pour l'utilisateur final :
 * l'écran d'édition les masque déjà via {@code allDataName} qui ne
 * remonte pas les nœuds racines avec enfants ( cas typique des
 * compositeReferences hiérarchiques ) .
 *
 * <p>Ces helpers servent à appliquer le MÊME masquage côté vue LISTE
 * ( {@code DataTypeAuthorizationsView} ) , de sorte que le compteur
 * "Liste des privilèges par données : (N)" et le détail du tableau
 * {@code SummaryTableOfAuthorizationByData} affichent uniquement les
 * entries explicitement cochées par l'utilisateur , et non les
 * dépendances techniques .
 *
 * <p>Pure : aucune dépendance Vue / Buefy / DOM . Testable unit
 * isolation .
 */

type AuthorizationEntry = { operationTypes?: string[] | null } | null | undefined;

type AuthorizationMap = Record<string, AuthorizationEntry>;

/**
 * Calcule l'ensemble des nœuds qui sont dépendances RLS implicites
 * d'au moins une entry "racine" présente dans {@code authorizations}
 * ( = un datatype/référentiel pour lequel l'utilisateur a coché au
 * moins une opération ) .
 *
 * <p>Utilise le graphe de dépendances {@code dependantNodesByDataName}
 * exposé par le backend dans le payload application ( clef = nom du
 * nœud principal , valeur = liste des nœuds dont il dépend ) .
 *
 * @param authorizations map des autorisations de la row
 * @param dependantNodesByDataName graphe de dépendances issu de
 *        {@code application.dependantNodesByDataName}
 * @returns ensemble des noms de nœuds dépendances ( jamais
 *          {@code null} , éventuellement vide )
 */
export function computeAutoInjectedDependencies(
  authorizations: AuthorizationMap | null | undefined,
  dependantNodesByDataName: Record<string, string[]> | null | undefined,
): Set<string> {
  const result = new Set<string>();
  if (!authorizations || !dependantNodesByDataName) return result;
  for (const [dataName, entry] of Object.entries(authorizations)) {
    const ops = entry?.operationTypes;
    if (!ops || ops.length === 0) continue;
    const deps = dependantNodesByDataName[dataName];
    if (!Array.isArray(deps)) continue;
    deps.forEach(d => result.add(d));
  }
  return result;
}

/**
 * Indique si l'entry {@code (dataName, entry)} est une dépendance
 * RLS auto-injectée par le backend ( = présente dans l'ensemble des
 * dépendances calculées par {@link computeAutoInjectedDependencies} ET
 * son set d'opérations se limite à {@code ["extraction"]} - signature
 * caractéristique de {@code addDependantAuthorizations} ) .
 *
 * <p>Les entries qui matchent ce critère sont à masquer de l'affichage
 * utilisateur ( vue liste ) pour rester cohérent avec la vue d'édition
 * qui ne les affiche pas non plus .
 */
export function isAutoInjectedDependency(
  dataName: string,
  entry: AuthorizationEntry,
  autoInjectedDependencies: Set<string>,
): boolean {
  if (!autoInjectedDependencies.has(dataName)) return false;
  const ops = entry?.operationTypes;
  if (!Array.isArray(ops) || ops.length !== 1) return false;
  return ops[0] === "extraction";
}

/**
 * Filtre une map d'autorisations pour ne conserver que les entries
 * **explicitement cochées par l'utilisateur** ( supprime les
 * dépendances RLS auto-injectées par {@code addDependantAuthorizations} ) .
 *
 * <p>Retourne un NOUVEAU map ( ne mute pas l'entrée ) , cohérent avec
 * le contrat WYSIWYG de l'écran d'édition .
 */
export function filterVisibleAuthorizations(
  authorizations: AuthorizationMap | null | undefined,
  dependantNodesByDataName: Record<string, string[]> | null | undefined,
): AuthorizationMap {
  if (!authorizations) return {};
  const autoInjected = computeAutoInjectedDependencies(authorizations, dependantNodesByDataName);
  const result: AuthorizationMap = {};
  for (const [dataName, entry] of Object.entries(authorizations)) {
    if (!isAutoInjectedDependency(dataName, entry, autoInjected)) {
      result[dataName] = entry;
    }
  }
  return result;
}
